import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Avatar, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RedeemIcon from '@mui/icons-material/Redeem';
import { getAdminAccessToken } from './AdminDashboard';

const API_URL = process.env.REACT_APP_API_URL;

function AdminRewardsDashboard({ getToken }) {
  const tokenGetter = getToken ? (async () => (typeof getToken === 'function' ? getToken() : getToken)) : getAdminAccessToken;
  const [pointsConfig, setPointsConfig] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editConfig, setEditConfig] = useState(null);
  const [voucherDialog, setVoucherDialog] = useState({ open: false, mode: 'add', voucher: {} });
  const [deletingVoucher, setDeletingVoucher] = useState(null);
  const [stipendReward, setStipendReward] = useState(0);
  const [stipendRewardEdit, setStipendRewardEdit] = useState(null);

  useEffect(() => {
    fetchAll();
    fetchStipendReward();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, vouchersRes, redemptionsRes] = await Promise.all([
        fetch(`${API_URL}/api/reward/config`),
        fetch(`${API_URL}/api/reward/vouchers`),
        fetch(`${API_URL}/api/reward/redemptions`)
      ]);
      const cfgData = await cfgRes.json();
      const vouchersData = await vouchersRes.json();
      const redemptionsData = await redemptionsRes.json();
      setPointsConfig(cfgData.configs || []);
      setVouchers(vouchersData.vouchers || []);
      setRedemptions(redemptionsData.redemptions || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load rewards data', severity: 'error' });
    }
    setLoading(false);
  };

  const fetchStipendReward = async () => {
    try {
      const token = await tokenGetter();
      const res = await fetch(`${API_URL}/api/admin/stipend-full-attendance-reward`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStipendReward(data.amount);
    } catch {}
  };

  // --- Points Config ---
  const handleEditConfig = (cfg) => setEditConfig({ ...cfg });
  const handleConfigChange = e => setEditConfig(c => ({ ...c, [e.target.name]: e.target.value }));
  const handleSaveConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reward/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configs: [editConfig] })
      });
      if (!res.ok) throw new Error('Failed to update config');
      setSnackbar({ open: true, message: 'Points config updated', severity: 'success' });
      setEditConfig(null);
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // --- Voucher Management ---
  const handleOpenVoucherDialog = (mode, voucher = {}) => setVoucherDialog({ open: true, mode, voucher });
  const handleCloseVoucherDialog = () => setVoucherDialog({ open: false, mode: 'add', voucher: {} });
  const handleVoucherChange = e => setVoucherDialog(d => ({ ...d, voucher: { ...d.voucher, [e.target.name]: e.target.value } }));
  const handleSaveVoucher = async () => {
    const { mode, voucher } = voucherDialog;
    try {
      const method = mode === 'add' ? 'POST' : 'PATCH';
      const url = mode === 'add' ? `${API_URL}/api/reward/vouchers` : `${API_URL}/api/reward/vouchers/${voucher.id}`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(voucher)
      });
      if (!res.ok) throw new Error('Failed to save voucher');
      setSnackbar({ open: true, message: `Voucher ${mode === 'add' ? 'added' : 'updated'}`, severity: 'success' });
      handleCloseVoucherDialog();
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };
  const handleDeleteVoucher = async (voucher) => {
    setDeletingVoucher(voucher.id);
    try {
      const res = await fetch(`${API_URL}/api/reward/vouchers/${voucher.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete voucher');
      setSnackbar({ open: true, message: 'Voucher deleted', severity: 'success' });
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
    setDeletingVoucher(null);
  };

  const handleEditStipendReward = () => setStipendRewardEdit(stipendReward);
  const handleSaveStipendReward = async () => {
    try {
      const token = await tokenGetter();
      const res = await fetch(`${API_URL}/api/admin/stipend-full-attendance-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount: Number(stipendRewardEdit) })
      });
      if (!res.ok) throw new Error('Failed to update config');
      setSnackbar({ open: true, message: 'Stipend reward config updated', severity: 'success' });
      setStipendRewardEdit(null);
      fetchStipendReward();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} mb={3} color="primary.main" textAlign="center">
        🛠️ Admin Rewards & Voucher Management
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Points Config Table */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Points Configuration</Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Activity</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pointsConfig.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">No config</TableCell></TableRow>
                    ) : pointsConfig.map(cfg => (
                      <TableRow key={cfg.id}>
                        <TableCell>{cfg.activity}</TableCell>
                        <TableCell>{cfg.points}</TableCell>
                        <TableCell>
                          <IconButton color="primary" onClick={() => handleEditConfig(cfg)}><EditIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Voucher Management */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Vouchers</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenVoucherDialog('add')}>Add Voucher</Button>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Image</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Required Points</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vouchers.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center">No vouchers</TableCell></TableRow>
                    ) : vouchers.map(v => (
                      <TableRow key={v.id}>
                        <TableCell><Avatar src={v.image_url} alt={v.name} /></TableCell>
                        <TableCell>{v.name}</TableCell>
                        <TableCell>{v.description}</TableCell>
                        <TableCell>{v.required_points}</TableCell>
                        <TableCell>{v.quantity}</TableCell>
                        <TableCell>{v.status || 'pending'}</TableCell>
                        <TableCell>
                          <IconButton color="primary" onClick={() => handleOpenVoucherDialog('edit', v)}><EditIcon /></IconButton>
                          <IconButton color="error" onClick={() => handleDeleteVoucher(v)} disabled={deletingVoucher === v.id}>{deletingVoucher === v.id ? <CircularProgress size={18} /> : <DeleteIcon />}</IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Redemption History */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Redemption History</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Candidate</TableCell>
                      <TableCell>Voucher</TableCell>
                      <TableCell>Voucher Code</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {redemptions.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">No redemptions</TableCell></TableRow>
                    ) : redemptions.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>{r.Candidate?.name || r.candidate_id}</TableCell>
                        <TableCell>{r.Voucher?.name || r.voucher_id}</TableCell>
                        <TableCell>{r.Voucher?.voucher_code || '-'}</TableCell>
                        <TableCell>{new Date(r.claimed_at).toLocaleDateString()}</TableCell>
                        <TableCell>{r.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Full Attendance Stipend Reward Points */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Full Attendance Stipend Reward Points</Typography>
                <Button variant="outlined" size="small" onClick={handleEditStipendReward}>Edit</Button>
              </Box>
              <Typography variant="body1">Current Value: <b>{stipendReward}</b> points</Typography>
            </CardContent>
          </Card>
        </>
      )}
      {/* Edit Points Config Dialog */}
      <Dialog open={!!editConfig} onClose={() => setEditConfig(null)}>
        <DialogTitle>Edit Points Config</DialogTitle>
        <DialogContent>
          <TextField
            label="Activity"
            name="activity"
            value={editConfig?.activity || ''}
            fullWidth
            margin="normal"
            disabled
          />
          <TextField
            label="Points"
            name="points"
            type="number"
            value={editConfig?.points || ''}
            onChange={handleConfigChange}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditConfig(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveConfig}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Add/Edit Voucher Dialog */}
      <Dialog open={voucherDialog.open} onClose={handleCloseVoucherDialog}>
        <DialogTitle>{voucherDialog.mode === 'add' ? 'Add Voucher' : 'Edit Voucher'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={voucherDialog.voucher.name || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            name="description"
            value={voucherDialog.voucher.description || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Image URL"
            name="image_url"
            value={voucherDialog.voucher.image_url || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Voucher Code"
            name="voucher_code"
            value={voucherDialog.voucher.voucher_code || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Required Points"
            name="required_points"
            type="number"
            value={voucherDialog.voucher.required_points || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Quantity"
            name="quantity"
            type="number"
            value={voucherDialog.voucher.quantity || ''}
            onChange={handleVoucherChange}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVoucherDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVoucher}>{voucherDialog.mode === 'add' ? 'Add' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={stipendRewardEdit !== null} onClose={() => setStipendRewardEdit(null)}>
        <DialogTitle>Edit Stipend Full Attendance Reward</DialogTitle>
        <DialogContent>
          <TextField
            label="Points"
            type="number"
            value={stipendRewardEdit ?? ''}
            onChange={e => setStipendRewardEdit(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStipendRewardEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStipendReward}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminRewardsDashboard; 